import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getNavigationItems, getNavigationBadges } from '@/lib/api/navigation-api';
import { hasPermission, type Permission, type NavItem, NAV_ITEMS } from '@/lib/rbac';
import type { UserRole } from '@/lib/rbac';
import { isApiReachable } from '@/lib/api/http-client';
import { LIVECHAT_PUBLIC_ORIGIN } from '@/lib/livechat-public-origin';

const LIVECHAT_PUBLIC_NAV: NavItem = {
  id: 'livechat-public',
  labelKey: 'nav.livechat',
  icon: 'ExternalLink',
  href: `${LIVECHAT_PUBLIC_ORIGIN}/livechat`,
  permission: 'live_chat.view',
  section: 'main',
};

interface NavigationState {
  items: NavItem[];
  lastFetched: number | null;
  lastRole: UserRole | null;
  lastStoreId: string | null;
  lastIsInternalTeam: boolean | null;
  lastIsEarlyAdopter: boolean | null;
  badgeCounts: { inbox: number; support_tickets: number };
  lastBadgeFetched: number | null;
  isLoading: boolean;
  error: string | null;
  fetchNavigation: (
    role: UserRole,
    isInternalTeam: boolean,
    isEarlyAdopter: boolean,
    storeId?: string,
    forceRefresh?: boolean
  ) => Promise<void>;
  fetchBadges: (storeId: string, forceRefresh?: boolean) => Promise<void>;
  clearCache: () => void;
}

// Cache TTL: 10 minutes (more aggressive than backend for better UX)
const CACHE_TTL_MS = 10 * 60 * 1000;

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set, get) => ({
      items: [],
      lastFetched: null,
      lastRole: null,
      lastStoreId: null,
      lastIsInternalTeam: null,
      lastIsEarlyAdopter: null,
      badgeCounts: { inbox: 0, support_tickets: 0 },
      lastBadgeFetched: null,
      isLoading: false,
      error: null,

      fetchNavigation: async (
        role: UserRole,
        isInternalTeam: boolean,
        isEarlyAdopter: boolean,
        storeId?: string,
        forceRefresh = false
      ) => {
        const state = get();
        const now = Date.now();

        // Invalidate cache if user context changed
        const contextChanged =
          state.lastRole !== role ||
          state.lastStoreId !== storeId ||
          state.lastIsInternalTeam !== isInternalTeam ||
          state.lastIsEarlyAdopter !== isEarlyAdopter;

        // Check if cache is still valid (unless force refresh or context changed)
        if (
          !forceRefresh &&
          !contextChanged &&
          state.lastFetched &&
          now - state.lastFetched < CACHE_TTL_MS &&
          state.items.length > 0
        ) {
          console.log('[Navigation Store] ✅ Using cached navigation items');
          return;
        }

        if (contextChanged) {
          console.log('[Navigation Store] 🔄 User context changed, invalidating cache');
        }

        set({ isLoading: true, error: null });

        // Check if API is reachable, if not use fallback immediately
        const apiReachable = await isApiReachable();
        if (!apiReachable) {
          const fallbackItems = NAV_ITEMS.filter((item) => hasPermission(role, item.permission));
          set({
            items: fallbackItems,
            lastFetched: now,
            lastRole: role,
            lastStoreId: storeId || null,
            lastIsInternalTeam: isInternalTeam,
            lastIsEarlyAdopter: isEarlyAdopter,
            isLoading: false,
            error: null,
          });
          console.log('[Navigation Store] ⚠️ API not reachable, using fallback navigation items');
          return;
        }

        try {
          // Fetch active items (for all users)
          const activeResponse = await getNavigationItems({
            status: 'active',
            store_id: storeId,
          });

          let allApiItems = [...activeResponse.items];
          const badgeCounts = activeResponse.badge_counts || { inbox: 0, support_tickets: 0 };

          // Determine if user should see inactive items (4-stage rollout)
          const shouldFetchInactive =
            isInternalTeam || isEarlyAdopter;

          if (shouldFetchInactive) {
            // Fetch inactive items
            const inactiveResponse = await getNavigationItems({
              status: 'inactive',
            });

            // Filter inactive items based on rollout flags
            const filteredInactiveItems = inactiveResponse.items.filter((item) => {
              // Internal team: developer and owner see all inactive; marketing sees only ready_for_internal_marketing
              if (isInternalTeam && (role === 'developer' || role === 'owner')) {
                return true;
              }
              if (isInternalTeam && role === 'marketing') {
                return item.ready_for_internal_marketing;
              }
              // Early adopter tenant: see only items ready for early adopters
              if (isEarlyAdopter && !isInternalTeam) {
                return item.ready_for_early_adopters;
              }
              return false;
            });

            allApiItems = [...allApiItems, ...filteredInactiveItems];
          }

          // Store badge counts as raw data
          set({ badgeCounts, lastBadgeFetched: now });

          // Convert API NavigationItem to NavItem format and filter by permission
          const convertedItems: NavItem[] = allApiItems
            .filter((item) => hasPermission(role, item.permission as Permission))
            .map((item) => {
              // Format badge for dynamic items (inbox, support-tickets) from badge_counts
              let badge: string | undefined = undefined;
              let badgeColor: string | undefined = item.badge_color || undefined;

              if (item.id === 'inbox' && badgeCounts.inbox > 0) {
                badge = badgeCounts.inbox > 99 ? '99+' : badgeCounts.inbox.toString();
                badgeColor = badgeColor || 'bg-red-600';
              } else if (item.id === 'support-tickets' && badgeCounts.support_tickets > 0) {
                badge = badgeCounts.support_tickets > 99 ? '99+' : badgeCounts.support_tickets.toString();
                badgeColor = badgeColor || 'bg-red-600';
              } else if (item.badge) {
                // Static badges from database (e.g., "New" for ai-chatbot)
                badge = item.badge;
                badgeColor = item.badge_color || undefined;
              }

              return {
                id: item.id,
                labelKey: item.label_key,
                icon: item.icon,
                href: item.href,
                permission: item.permission as Permission,
                badge,
                badgeColor,
                section: item.section,
              };
            })
            .sort((a, b) => {
              // Sort by section first, then by display_order
              const sectionOrder = a.section === 'main' ? 0 : 1;
              const sectionOrderB = b.section === 'main' ? 0 : 1;
              if (sectionOrder !== sectionOrderB) return sectionOrder - sectionOrderB;

              const itemA = allApiItems.find((i) => i.id === a.id);
              const itemB = allApiItems.find((i) => i.id === b.id);
              return (itemA?.display_order || 0) - (itemB?.display_order || 0);
            });

          let itemsForNav = convertedItems;
          if (
            hasPermission(role, 'live_chat.view') &&
            !convertedItems.some((i) => i.id === 'livechat-public')
          ) {
            const afterWidget = convertedItems.findIndex((i) => i.id === 'live-chat-widget');
            if (afterWidget >= 0) {
              itemsForNav = [
                ...convertedItems.slice(0, afterWidget + 1),
                LIVECHAT_PUBLIC_NAV,
                ...convertedItems.slice(afterWidget + 1),
              ];
            } else {
              itemsForNav = [...convertedItems, LIVECHAT_PUBLIC_NAV];
            }
          }

          set({
            items: itemsForNav,
            lastFetched: now,
            lastRole: role,
            lastStoreId: storeId || null,
            lastIsInternalTeam: isInternalTeam,
            lastIsEarlyAdopter: isEarlyAdopter,
            isLoading: false,
            error: null,
          });

          console.log('[Navigation Store] ✅ Loaded navigation items from API', {
            total: convertedItems.length,
            isInternalTeam,
            isEarlyAdopter,
            role,
          });
        } catch (error) {
          console.error('[Navigation Store] Failed to load navigation items:', error);

          // Fallback to hardcoded NAV_ITEMS if API fails
          const fallbackItems = NAV_ITEMS.filter((item) => hasPermission(role, item.permission));
          set({
            items: fallbackItems,
            lastFetched: now,
            lastRole: role,
            lastStoreId: storeId || null,
            lastIsInternalTeam: isInternalTeam,
            lastIsEarlyAdopter: isEarlyAdopter,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load navigation',
          });

          console.log('[Navigation Store] ⚠️ Using fallback navigation items');
        }
      },

      fetchBadges: async (storeId: string, forceRefresh = false) => {
        const state = get();
        const now = Date.now();

        // Check if badge cache is still valid (2 minutes TTL for badges - more frequent refresh)
        const BADGE_CACHE_TTL_MS = 2 * 60 * 1000;
        if (!forceRefresh && state.lastBadgeFetched && (now - state.lastBadgeFetched) < BADGE_CACHE_TTL_MS) {
          return;
        }

        try {
          // Fetch badge counts from dedicated endpoint
          const badgeCounts = await getNavigationBadges(storeId);

          // Update items with formatted badges from badge counts
          const updatedItems = state.items.map(item => {
            if (item.id === 'inbox') {
              const badge = badgeCounts.inbox > 0 
                ? (badgeCounts.inbox > 99 ? '99+' : badgeCounts.inbox.toString())
                : undefined;
              return {
                ...item,
                badge,
                badgeColor: badge ? (item.badgeColor || 'bg-red-600') : undefined,
              };
            } else if (item.id === 'support-tickets') {
              const badge = badgeCounts.support_tickets > 0
                ? (badgeCounts.support_tickets > 99 ? '99+' : badgeCounts.support_tickets.toString())
                : undefined;
              return {
                ...item,
                badge,
                badgeColor: badge ? (item.badgeColor || 'bg-red-600') : undefined,
              };
            }
            return item;
          });

          set({
            items: updatedItems,
            badgeCounts,
            lastBadgeFetched: now,
          });
        } catch (error) {
          console.warn('[Navigation Store] Failed to fetch badge counts:', error);
        }
      },

      clearCache: () => {
        set({
          items: [],
          lastFetched: null,
          lastRole: null,
          lastStoreId: null,
          lastIsInternalTeam: null,
          lastIsEarlyAdopter: null,
          badgeCounts: { inbox: 0, support_tickets: 0 },
          lastBadgeFetched: null,
          error: null,
        });
      },
    }),
    {
      name: 'kustomer-navigation',
      partialize: (state) => ({
        items: state.items,
        lastFetched: state.lastFetched,
        lastRole: state.lastRole,
        lastStoreId: state.lastStoreId,
        lastIsInternalTeam: state.lastIsInternalTeam,
        lastIsEarlyAdopter: state.lastIsEarlyAdopter,
        badgeCounts: state.badgeCounts,
        lastBadgeFetched: state.lastBadgeFetched,
      }),
    }
  )
);
